/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2018-2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
*/

angular.module('bulbApp.contentEditorModule')
.service('BulbImageCarouselService', [
  '$log', '$q', '$window', 'BulbAssetLibraryGoogleDriveService', 'BulbAssetLibraryGooglePhotoService',
  'BulbAssetLibraryService', 'BulbAssetLibraryUnsplashService', 'BulbAssetLibraryUploadService',
  'BulbAssetLibraryOneDriveService', 'BulbBusyService', 'BulbBusyCoverService', 'BulbCKEStateService', 'CONFIG', BulbImageCarouselService]);

/**
 * Handles certain features of the bulb-carousel. These include: changing the state of the carousel, and
 * handling how the carousel looks.
 */
function BulbImageCarouselService(
  $log,
  $q,
  $window,
  BulbAssetLibraryGoogleDriveService,
  BulbAssetLibraryGooglePhotoService,
  BulbAssetLibraryService,
  BulbAssetLibraryUnsplashService,
  BulbAssetLibraryUploadService,
  BulbAssetLibraryOneDriveService,
  BulbBusyService,
  BulbBusyCoverService,
  BulbCKEStateService,
  CONFIG,
) {
  const that = this;
  $window.bulbVars.BulbImageCarouselService = this;

  /**
   * Function that vertically and horizontally aligns the thumbnail images.
   * @param slick The current instance of Slick
   */
  this.checkSlickNoSlide = function (slick) {
    // updating slick count comparison from less than to less than equals slides to show.
    if (slick.slideCount <= slick.options.slidesToShow) {
      // Adding no-dots css for hiding unwanted single dot if slide count is <= slidesToShow.
      slick.$dots.addClass('no-dots');
      slick.$slider.addClass('slick-no-slide');
    } else {
      slick.$slider.removeClass('slick-no-slide');
    }
  };

  /**
   * Function that finds the current thumbnail within the image carousel.
   * @param slick The current Slick instance.
   */
  this.grabCurrentThumbnailSlide = function (slick) {
    // We will use the current slick instance to grab the current carousel thumbnail
    for (let i = 0; i < slick.$slides.length; i += 1) {
      const slide = slick.$slides[i];
      if (slide.classList.contains('slick-current')) {
        $(slide).find('img').addClass('bulb-carousel-current-thumbnail-image');
      } else {
        $(slide).find('img').removeClass('bulb-carousel-current-thumbnail-image');
      }
    }
  };

  /**
   * Function that finds the current display image within the image carousel.
   * @param slick The current Slick instance.
   * @param currentImage The currentImage that is displayed in the image carousel
   */
  this.grabCurrentDisplaySlide = function (slick, currentImage) {
    // We will use the current slick instance to grab the current carousel thumbnail
    const imageAsset = currentImage.scaledImageAsset || currentImage.fullImageAsset;
    for (let i = 0; i < slick.$slides.length; i += 1) {
      const slide = slick.$slides[i];
      if ($(slide).find('img')[0]) {
        if (imageAsset.url === $(slide).find('img')[0].src) {
          $(slide).find('img').addClass('bulb-carousel-current-thumbnail-image');
        } else {
          $(slide).find('img').removeClass('bulb-carousel-current-thumbnail-image');
        }
      }
    }
  };

  /**
   * This helper function will be able to go into the DOM and find an image inside of an image carousel using just
   * the content block's ID and the index of the image inside of the carousel. This is used to perform crop and rotate
   * procedures on the images.
   * @param index The index of the image inside of the carousel-block.
   * @param id The id of the image carousel to look inside of.
   * @returns {JQuery<HTMLElement> | jQuery | HTMLElement}
   */
  this.findElementByIndexAndId = function (index, id) {
    const $carousel = $(`#carousel-${id}`);
    const $li = $carousel.find(`li[array-index="${index}"]`);
    const $image = $li.find('img');

    return $image;
  };

  this.submitFileHandler = function (event, data) {
    const showBusyIndicator = BulbBusyService.showBusy();
    data.showBusyIndicator = showBusyIndicator;
  };

  this.doneFileHandler = function (event, data) {
    const entryId = data.entry.id;
    BulbAssetLibraryService.api.getEntry(entryId).then((res) => {
      if (event === 'google-import' || event === 'google-photo-import' || event === 'unsplash-import'
        || event === 'onedrive-import' || event === 'draganddrop') {
        // What should do, anything?
        $log.debug(`${event}event for image carousel`);
      } else {
        // Need to close the current modal twice because there are, at this point, two modals that have
        // been opened.
        BulbAssetLibraryService.dialog.closeCurrentModal();
        BulbAssetLibraryService.dialog.closeCurrentModal();
        data.showBusyIndicator();
      }
      if (res.entry.image) {
        res.entry.image.assetLibraryEntryId = res.entry.id;
        that.addCarouselImageDeferred.resolve(res.entry.image);
      }
    }, (reason) => {
      $log.error('unable to upload image to carousel form desktop because of reason: ', reason);
    });
  };

  /**
   * This function is used to show busy indicator during upload.
   */
  this.stopBusyIndicator = function() {
    let stopBusyIndicatorCallback = null;
    stopBusyIndicatorCallback = BulbBusyCoverService.showBusy();
    BulbCKEStateService.stopBusyIndicator.next(stopBusyIndicatorCallback);
  };

  /**
   * This function is used to handle error when file is unable to process.
   */
  this.errorFileHandler = function (data) {
    data.showBusyIndicator();
  };

  this.addImageToCarousel = function () {
    // This function adds an image to the carousel. The context of this function is quite interesting, in
    // that I give the function it's own deferred action within it's own scope. I then bind its scope, 'self',
    // to the helper functions so that way, I can resolve the action at a later time, allowing the correct
    // workflow of adding an image to the carousel.
    const self = that;
    self.addCarouselImageDeferred = $q.defer();

    const assetSelectHandler = imageCarouselSelectHandler.bind(self);
    const desktopUploadSelectHandler = desktopUploadSelectHandlerFunc.bind(self);
    const googleImportSelectHandler = googleImportSelectHandlerFunc.bind(self);
    const googlePhotoImportSelectHandler = googlePhotoImportSelectHandlerFunc.bind(self);
    const unsplashSelectHandler = unsplashSelectHandlerFunc.bind(self);
    const oneDriveSelectHandler = oneDriveSelectHandlerFunc.bind(self);

    // The BulbAssetLibraryService showSelectionModal takes callback functions that handle the addition of
    // images. There is a callback function for the assetlibrary, uploading from desktop, google import, and
    // drag and drop.
    BulbAssetLibraryService.dialog.image.showSelectionModal(
      assetSelectHandler,
      desktopUploadSelectHandler,
      googleImportSelectHandler,
      googlePhotoImportSelectHandler,
      unsplashSelectHandler,
      oneDriveSelectHandler,
      BulbAssetLibraryUploadService.createImageUploadOptions( // drag and drop
        null,
        self,
        '',
        'submitFileHandler',
        '',
        'doneFileHandler',
      ),
      true,
    );

    return self.addCarouselImageDeferred.promise;
  };

  // This function is the asset library helper function that gets passed to the showSelectionModal function
  // from the BulbAssetLibraryService. It is actually the callback function for dealing with the selection
  // of an image from the assetlibrary selection modal.
  function imageCarouselSelectHandler(entry) {
    // I am resolving the addCarouselImageDeferred action here so that way the assetlibrary entry image
    // gets passed to the command-add-carousel-slide event above.
    // 'that' is actually the context / scope of the 'addImageToCarousel' function defined below.
    entry.image.assetLibraryEntryId = entry.id;
    that.addCarouselImageDeferred.resolve(entry.image);
    return that.addCarouselImageDeferred.promise;
  }

  function desktopUploadSelectHandlerFunc() {
    // Image carousel is uploaded from Desktop.
    BulbCKEStateService.setDesktopUploadSelectionMode(true);
    BulbAssetLibraryUploadService.uploadImageBySelectionModal( // from desktop
      that,
      '',
      'submitFileHandler',
      '',
      'doneFileHandler',
      false, // from within assetlibrary
      'pageImageCarousel', // upload state
    );
  }

  function googleImportSelectHandlerFunc() {
    BulbAssetLibraryService.dialog.closeCurrentModal(); // google drive
    BulbAssetLibraryGoogleDriveService.openImageTypePicker(
      (oauthToken, id, mimeType, photoParentId) => {
        BulbBusyCoverService.showBusyDuring(
          BulbAssetLibraryService.api.importFromGoogleDrive(
            oauthToken,
            null,
            id,
            mimeType,
            photoParentId,
          ).then(
            (googleResponseData) => {
              that.stopBusyIndicator();
              that.doneFileHandler('google-import', googleResponseData);
            },
          ),
        );
      },
    );
  }

  function googlePhotoImportSelectHandlerFunc() {
    BulbAssetLibraryService.dialog.closeCurrentModal();
    BulbAssetLibraryGooglePhotoService.initializeGooglePhotoPicker(
      (oauthToken, mediaId) => {
        BulbBusyCoverService.showBusyDuring(
          BulbAssetLibraryService.api.importFromGooglePhoto(
            oauthToken,
            null,
            mediaId,
          ).then(
            (data) => {
              that.stopBusyIndicator();
              that.doneFileHandler('google-photo-import', data);
            },
          ),
        );
      },
    );
  }

  // add image to carousel via unsplash.
  function unsplashSelectHandlerFunc() {
    BulbAssetLibraryUnsplashService.initializeUnsplashPhotoPicker(
      (photoId) => {
        BulbBusyCoverService.showBusyDuring(
          BulbAssetLibraryUnsplashService.importFromUnsplash(photoId, null).then(
            (unsplashResponseData) => {
              that.stopBusyIndicator();
              that.doneFileHandler('unsplash-import', unsplashResponseData);
              BulbAssetLibraryService.dialog.closeCurrentModal();
            },
          ),
        );
      },
    );
  }

  /**
   * Add images to carousal via OneDrive
   *
   */
  function oneDriveSelectHandlerFunc() {
    BulbAssetLibraryService.dialog.closeCurrentModal();
    BulbAssetLibraryOneDriveService.initializeOneDrivePicker((oneDriveFileObject) => {
      BulbBusyCoverService.showBusyDuring(
        BulbAssetLibraryOneDriveService.importFromOneDrive(oneDriveFileObject.id, null).then(
          (oneDriveResponseData) => {
            that.stopBusyIndicator();
            that.doneFileHandler('onedrive-import', oneDriveResponseData);
          },
        ),
      );
    }, [CONFIG.image.extensions]);
  }
}
